import React, { useState, useEffect, useCallback } from "react";
import EventsService from "../services/EventsService";
import {
  ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, CartesianGrid, Cell,
} from "recharts";
import { format } from "date-fns";

const actionsColorMap = [
  "#1E8E3A", "#0A84FF", "#C62828", "#B8960A",
  "#8E44AD", "#E67E22", "#00BCD4", "#E91E63", "#3F51B5", "#7CB342",
];

function ChartsPage() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [chartData, setChartData] = useState(null);
  const [freqData, setFreqData] = useState(null);
  const [intervalData, setIntervalData] = useState(null);
  const [selectedActionType, setSelectedActionType] = useState("");
  const [activeTab, setActiveTab] = useState("scatter");
  const [loading, setLoading] = useState(false);

  // Cross-event state
  const [crossEvent1, setCrossEvent1] = useState("");
  const [crossAction1, setCrossAction1] = useState("");
  const [crossEvent2, setCrossEvent2] = useState("");
  const [crossAction2, setCrossAction2] = useState("");
  const [correlationData, setCorrelationData] = useState(null);
  const [crossLoading, setCrossLoading] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await EventsService.getAllEvents();
        setEvents(data.filter((e) => e.started));
      } catch (err) {
        console.error(err.message);
      }
    };
    fetchEvents();
  }, []);

  const fetchCharts = useCallback(async () => {
    if (!selectedEvent) return;
    setLoading(true);
    try {
      const [chart, freq] = await Promise.all([
        EventsService.getEventChart(selectedEvent),
        EventsService.getActionFrequency(selectedEvent),
      ]);
      setChartData(chart);
      setFreqData(freq);
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedEvent]);

  useEffect(() => {
    fetchCharts();
    setIntervalData(null);
    setSelectedActionType("");
  }, [fetchCharts]);

  const fetchInterval = async (typeId) => {
    setSelectedActionType(typeId);
    if (!typeId) return;
    try {
      const res = await EventsService.getIntervalDistribution(selectedEvent, typeId);
      setIntervalData(res);
    } catch (err) {
      console.error(err.message);
    }
  };

  const fetchCorrelation = async () => {
    if (!crossEvent1 || !crossAction1 || !crossEvent2 || !crossAction2) return;
    setCrossLoading(true);
    try {
      const res = await EventsService.getCorrelations({
        event1: crossEvent1, action1: crossAction1,
        event2: crossEvent2, action2: crossAction2,
      });
      setCorrelationData(res);
    } catch (err) {
      console.error(err.message);
    } finally {
      setCrossLoading(false);
    }
  };

  const eventObj = events.find((e) => e.id === selectedEvent);

  const rawChart = Array.isArray(chartData) ? chartData : chartData?.points || [];
  const typeIds = [...new Set(rawChart.map((p) => p.typeId))];
  const scatterPoints = rawChart.map((p, i) => ({
    x: new Date(p.time).getTime(),
    y: typeIds.indexOf(p.typeId),
    actionName: p.actionName,
    color: actionsColorMap[typeIds.indexOf(p.typeId) % actionsColorMap.length],
  }));

  const rawFreq = freqData?.perAction || freqData?.actions || [];
  const freqBars = rawFreq.map((a, i) => ({
    name: a.typeId || a.name || `Action ${i + 1}`,
    count: a.count || 0,
    color: actionsColorMap[i % actionsColorMap.length],
  }));

  const rawIntervals = Array.isArray(intervalData) ? intervalData : intervalData?.buckets || [];
  const intervalBars = (() => {
    if (rawIntervals.length === 0) return [];
    if (typeof rawIntervals[0] === "number") {
      const buckets = { "0-5s": 0, "5-15s": 0, "15-30s": 0, "30-60s": 0, "1-5m": 0, "5m+": 0 };
      for (const sec of rawIntervals) {
        if (sec <= 5) buckets["0-5s"]++;
        else if (sec <= 15) buckets["5-15s"]++;
        else if (sec <= 30) buckets["15-30s"]++;
        else if (sec <= 60) buckets["30-60s"]++;
        else if (sec <= 300) buckets["1-5m"]++;
        else buckets["5m+"]++;
      }
      return Object.entries(buckets).map(([range, count]) => ({ range, count }));
    }
    return rawIntervals.map((b) => ({ range: b.label || b.range, count: b.count }));
  })();

  return (
    <>
      <div className="page-header">
        <nav className="navbar navbar-expand-lg d-flex justify-content-between">
          <div className="header-title flex-fill">
            <a
              id="sidebar-toggle"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.__toggleSidebar && window.__toggleSidebar(e);
              }}
            >
              <i className="fas fa-bars"></i>
            </a>
            <h5>Charts &amp; Analytics</h5>
          </div>
        </nav>
      </div>
      <div className="main-wrapper">
        {/* Event Selector */}
        <div className="glass-card" style={{ padding: 20, marginBottom: 24 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, display: "block" }}>
            Select Activity
          </label>
          <select
            className="form-control"
            value={selectedEvent}
            onChange={(e) => setSelectedEvent(e.target.value)}
            style={{ maxWidth: 400 }}
          >
            <option value="">Choose an activity...</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.name}</option>
            ))}
          </select>
        </div>

        {selectedEvent && (
          <>
            {/* Chart Tabs */}
            <div className="timeline-tabs" style={{ marginBottom: 20 }}>
              {[
                { key: "scatter", label: "Timeline" },
                { key: "frequency", label: "Frequency" },
                { key: "interval", label: "Intervals" },
                { key: "cross", label: "Cross-Activity" },
              ].map((t) => (
                <button
                  key={t.key}
                  className={`timeline-tab ${activeTab === t.key ? "timeline-tab-active" : ""}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {loading && (
              <div className="d-flex justify-content-center" style={{ padding: 60 }}>
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            {!loading && activeTab === "scatter" && (
              <div className="glass-card" style={{ padding: 24 }}>
                <h6 className="event-section-title" style={{ marginBottom: 16 }}>
                  Action Timeline
                </h6>
                {scatterPoints.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis
                        dataKey="x"
                        type="number"
                        domain={["dataMin", "dataMax"]}
                        tickFormatter={(v) => format(new Date(v), "HH:mm")}
                        stroke="rgba(255,255,255,0.3)"
                        tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }}
                      />
                      <YAxis
                        dataKey="y"
                        type="number"
                        stroke="rgba(255,255,255,0.3)"
                        tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }}
                        tickFormatter={(v) => {
                          const a = eventObj?.actions?.[v];
                          return a?.name || `#${v}`;
                        }}
                      />
                      <Tooltip
                        content={({ payload }) => {
                          if (!payload || !payload[0]) return null;
                          const d = payload[0].payload;
                          return (
                            <div className="chart-tooltip">
                              <div>{d.actionName}</div>
                              <div style={{ fontSize: 11, opacity: 0.6 }}>
                                {format(new Date(d.x), "HH:mm:ss")}
                              </div>
                            </div>
                          );
                        }}
                      />
                      <Scatter data={scatterPoints} shape={(props) => {
                        const { cx, cy, payload } = props;
                        return (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={5}
                            fill={payload.color}
                            fillOpacity={0.8}
                            stroke={payload.color}
                            strokeWidth={1}
                          />
                        );
                      }} />
                    </ScatterChart>
                  </ResponsiveContainer>
                ) : (
                  <p style={{ color: "rgba(255,255,255,0.3)", textAlign: "center" }}>
                    No chart data available.
                  </p>
                )}
              </div>
            )}

            {!loading && activeTab === "frequency" && (
              <div className="glass-card" style={{ padding: 24 }}>
                <h6 className="event-section-title" style={{ marginBottom: 16 }}>
                  Action Frequency
                </h6>
                {freqBars.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={freqBars} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis
                        dataKey="name"
                        stroke="rgba(255,255,255,0.3)"
                        tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }}
                      />
                      <YAxis
                        stroke="rgba(255,255,255,0.3)"
                        tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "rgba(15,15,25,0.95)",
                          border: "0.5px solid rgba(255,255,255,0.12)",
                          borderRadius: 10,
                          color: "#fff",
                        }}
                      />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {freqBars.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p style={{ color: "rgba(255,255,255,0.3)", textAlign: "center" }}>
                    No frequency data available.
                  </p>
                )}
              </div>
            )}

            {!loading && activeTab === "interval" && (
              <div className="glass-card" style={{ padding: 24 }}>
                <h6 className="event-section-title" style={{ marginBottom: 16 }}>
                  Interval Distribution
                </h6>
                <div style={{ marginBottom: 16 }}>
                  <select
                    className="form-control"
                    value={selectedActionType}
                    onChange={(e) => fetchInterval(e.target.value)}
                    style={{ maxWidth: 300 }}
                  >
                    <option value="">Select action...</option>
                    {eventObj?.actions?.map((a, i) => (
                      <option key={a.id || i} value={a.typeId}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>
                {intervalBars.length > 0 ? (
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={intervalBars} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis
                        dataKey="range"
                        stroke="rgba(255,255,255,0.3)"
                        tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }}
                        angle={-30}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis
                        stroke="rgba(255,255,255,0.3)"
                        tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "rgba(15,15,25,0.95)",
                          border: "0.5px solid rgba(255,255,255,0.12)",
                          borderRadius: 10,
                          color: "#fff",
                        }}
                      />
                      <Bar dataKey="count" fill="#0A84FF" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p style={{ color: "rgba(255,255,255,0.3)", textAlign: "center" }}>
                    {selectedActionType
                      ? "No interval data available."
                      : "Select an action to see interval distribution."}
                  </p>
                )}
              </div>
            )}

            {!loading && activeTab === "cross" && (
              <div className="glass-card" style={{ padding: 24 }}>
                <h6 className="event-section-title" style={{ marginBottom: 16 }}>
                  Cross-Activity Correlation
                </h6>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                  <div style={{ flex: "1 1 200px" }}>
                    <label className="chart-select-label">Activity 1</label>
                    <select
                      className="form-control"
                      value={crossEvent1}
                      onChange={(e) => { setCrossEvent1(e.target.value); setCrossAction1(""); }}
                    >
                      <option value="">Select...</option>
                      {events.map((ev) => (
                        <option key={ev.id} value={ev.id}>{ev.name}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: "1 1 200px" }}>
                    <label className="chart-select-label">Action 1</label>
                    <select
                      className="form-control"
                      value={crossAction1}
                      onChange={(e) => setCrossAction1(e.target.value)}
                    >
                      <option value="">Select...</option>
                      {events.find((e) => e.id === crossEvent1)?.actions?.map((a) => (
                        <option key={a.id} value={a.typeId}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: "1 1 200px" }}>
                    <label className="chart-select-label">Activity 2</label>
                    <select
                      className="form-control"
                      value={crossEvent2}
                      onChange={(e) => { setCrossEvent2(e.target.value); setCrossAction2(""); }}
                    >
                      <option value="">Select...</option>
                      {events.map((ev) => (
                        <option key={ev.id} value={ev.id}>{ev.name}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: "1 1 200px" }}>
                    <label className="chart-select-label">Action 2</label>
                    <select
                      className="form-control"
                      value={crossAction2}
                      onChange={(e) => setCrossAction2(e.target.value)}
                    >
                      <option value="">Select...</option>
                      {events.find((e) => e.id === crossEvent2)?.actions?.map((a) => (
                        <option key={a.id} value={a.typeId}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  className="btn btn-info"
                  onClick={fetchCorrelation}
                  disabled={crossLoading || !crossAction1 || !crossAction2}
                  style={{ marginBottom: 20 }}
                >
                  {crossLoading ? "Analyzing..." : "Analyze Correlation"}
                </button>

                {correlationData && (
                  <div>
                    {correlationData.pearson !== undefined && (
                      <div className="glass-card" style={{ padding: 16, marginBottom: 16, display: "inline-block" }}>
                        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                          Pearson Coefficient
                        </span>
                        <p style={{ fontSize: 32, fontWeight: 800, color: "#0A84FF", margin: "4px 0 0" }}>
                          {typeof correlationData.pearson === "number"
                            ? correlationData.pearson.toFixed(4)
                            : correlationData.pearson}
                        </p>
                      </div>
                    )}
                    {correlationData.points && correlationData.points.length > 0 && (
                      <ResponsiveContainer width="100%" height={350}>
                        <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis
                            dataKey="x"
                            type="number"
                            stroke="rgba(255,255,255,0.3)"
                            tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }}
                            name="Action 1"
                          />
                          <YAxis
                            dataKey="y"
                            type="number"
                            stroke="rgba(255,255,255,0.3)"
                            tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }}
                            name="Action 2"
                          />
                          <Tooltip
                            contentStyle={{
                              background: "rgba(15,15,25,0.95)",
                              border: "0.5px solid rgba(255,255,255,0.12)",
                              borderRadius: 10,
                              color: "#fff",
                            }}
                          />
                          <Scatter data={correlationData.points} fill="#E67E22" fillOpacity={0.7} />
                        </ScatterChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default ChartsPage;
